import React, { useId } from 'react';
import { formatPrice } from '../../utils';
import { DEFAULT_APR_PERCENT } from '../../hooks/useOfferForm';
import './FinancingStep.css';

// 3.5% is the usual FHA minimum; the down payment now sets the loan amount the
// offer's financing contingency names (QA N13).
const DP_PRESETS = [3.5, 5, 10, 20, 25];
const EARNEST_PRESETS = [0.01, 0.02, 0.03];

export default function FinancingStep({ wiz, property }) {
  // Earnest money is a percentage of what the BUYER is offering, not of what
  // the seller is asking — the same basis the down payment already uses. Using
  // the list price quoted "1% · $4,250" on a $415,000 offer against a $425,000
  // listing. Fall back to the list price only while the offer price is unset.
  const offerPrice = wiz.derived.offerPrice || property?.price || property?.list_price || 0;
  const dpPct = wiz.derived.dpPct;
  const dpAmount = wiz.derived.dpAmount;
  const financed = wiz.derived.financedAmount;
  const monthly = wiz.derived.monthlyPI;
  const earnest = wiz.derived.earnest;

  const dpRangeId = useId();
  const earnestId = useId();
  const aprId = useId();

  return (
    <section className="financing-step">
      <header className="financing-step__intro">
        <h3>How will you finance this purchase?</h3>
        <p>Choose your down payment and earnest money. We&apos;ll show your estimated monthly cost.</p>
      </header>

      <div className="financing-step__field">
        <span className="financing-step__label" id={`${dpRangeId}-label`}>Down payment</span>
        <div className="financing-step__chips" role="group" aria-labelledby={`${dpRangeId}-label`}>
          {DP_PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              className={`financing-step__chip${dpPct === p ? ' financing-step__chip--active' : ''}`}
              onClick={() => wiz.setField('down_payment_percent', p)}
            >
              {p}%
            </button>
          ))}
        </div>

        <div className="financing-step__slider-row">
          <input
            id={dpRangeId}
            type="range"
            min="0"
            max="100"
            step="0.5"
            value={dpPct}
            onChange={(e) => wiz.setField('down_payment_percent', parseFloat(e.target.value))}
            className="financing-step__slider"
            aria-label="Down payment percentage"
          />
          <div className="financing-step__slider-readout">
            <strong>{dpPct}%</strong>
            <span>{formatPrice(dpAmount)}</span>
          </div>
        </div>
      </div>

      {financed > 0 && (
        <div className="financing-step__estimate" aria-live="polite">
          <div className="financing-step__estimate-row">
            <span>Financed amount</span>
            <strong>{formatPrice(financed)}</strong>
          </div>
          <div className="financing-step__estimate-row financing-step__estimate-row--accent">
            <span>Estimated monthly P&amp;I</span>
            <strong>{formatPrice(monthly)}/mo</strong>
          </div>
          <div className="financing-step__apr-row">
            <label htmlFor={aprId}>Interest rate (APR)</label>
            <div className="financing-step__apr-input-wrap">
              <input
                id={aprId}
                type="number"
                inputMode="decimal"
                min="0"
                max="20"
                step="0.125"
                value={wiz.aprPercent}
                onChange={(e) => {
                  const raw = e.target.value;
                  // Allow clearing while typing; clamp anything out of range.
                  if (raw === '' || (Number(raw) >= 0 && Number(raw) <= 20)) wiz.setAprPercent(raw);
                }}
                onBlur={() => { if (wiz.aprPercent === '') wiz.setAprPercent(DEFAULT_APR_PERCENT); }}
                className="financing-step__apr-input"
              />
              <span aria-hidden="true">%</span>
            </div>
          </div>
          <p className="financing-step__estimate-note">
            Estimate based on a 30-yr fixed loan at {Number(wiz.derived.aprUsed) || 0}% APR. Excludes taxes, insurance, HOA.
          </p>
        </div>
      )}

      <div className="financing-step__field">
        <label htmlFor={earnestId} className="financing-step__label">Earnest money</label>
        <div className="financing-step__chips">
          {offerPrice > 0 && EARNEST_PRESETS.map((pct) => {
            const amount = Math.round(offerPrice * pct);
            const active = earnest === amount;
            return (
              <button
                key={pct}
                type="button"
                className={`financing-step__chip${active ? ' financing-step__chip--active' : ''}`}
                onClick={() => wiz.setField('earnest_money', amount)}
              >
                {Math.round(pct * 100)}% · {formatPrice(amount)}
              </button>
            );
          })}
          <button
            type="button"
            className={`financing-step__chip${earnest === 0 ? ' financing-step__chip--active' : ''}`}
            onClick={() => wiz.setField('earnest_money', '')}
          >
            Skip
          </button>
        </div>
        <div className="financing-step__input-wrap">
          <span className="financing-step__prefix" aria-hidden="true">$</span>
          <input
            id={earnestId}
            type="text"
            inputMode="numeric"
            value={wiz.form.earnest_money ? Number(wiz.form.earnest_money).toLocaleString('en-US') : ''}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^0-9]/g, '');
              wiz.setField('earnest_money', raw ? parseInt(raw, 10) : '');
            }}
            placeholder="Custom amount"
            className="financing-step__input"
          />
        </div>
        <p className="financing-step__hint">Good-faith deposit, typically 1–3% of price.</p>
        <div className="financing-step__escrow-note" role="note" data-testid="earnest-escrow-note">
          <p className="financing-step__escrow-title">
            Keep earnest money in escrow, not with the seller
          </p>
          <p>
            Work with your title company or another escrow agent, such as a real estate
            attorney, to hold your earnest money in escrow. Name that holder and the
            deposit deadline in your offer, and get a written escrow agreement. Tell your lender the amount
            and when you&apos;ll pay it, because lenders usually need to document where
            those funds came from. cutthe6.com does not hold or transfer earnest money.
          </p>
          <p>
            <strong>Before you send any money,</strong> call your title company at a number
            you look up yourself to confirm the wire instructions. Wire fraud that targets
            home buyers is common.
          </p>
          <p className="financing-step__escrow-disclaimer">
            General information only, not legal advice. Rules vary by state, so consult
            a licensed real estate attorney.
          </p>
        </div>
      </div>
    </section>
  );
}
